import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Loader2, Shield, LogOut, X, Copy, Trash2, Building2, Ticket } from 'lucide-react';
import { getApiBase, formatApiFetchError } from '@/lib/apiBase';
import { BRAND_NAME, getBrandFallbackMarkSrc, getBrandLogoSrc } from '@/lib/brandConstants';
import BrandFormChrome from '@/components/BrandFormChrome';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** Resolve at call time so production builds pick up `REACT_APP_API_URL` from env at build. */
function apiBase() {
  return getApiBase();
}

const ROLES = ['CTO', 'CEO', 'CMO', 'COO'];

/** Leadership names on the executive dashboard (display only). */
const ROLE_LEAD_NAME = {
  CTO: 'Anish | Product & AI Systems',
  CEO: 'Ankit | Growth & Partnerships',
  CMO: 'Rohit | Brand & Growth',
  COO: 'Bhumi | Operations & Experience',
};

const IST = 'Asia/Kolkata';

/** All displayed timestamps use India Standard Time. */
function formatIst(isoOrString) {
  if (!isoOrString) return '—';
  const d = typeof isoOrString === 'string' ? new Date(isoOrString) : isoOrString;
  if (Number.isNaN(d.getTime())) return String(isoOrString);
  return d.toLocaleString('en-IN', {
    timeZone: IST,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

function normalizeKey(raw) {
  return String(raw || '')
    .replace(/\uFEFF/g, '')
    .replace(/\u200B/g, '')
    .replace(/\r/g, '')
    .trim();
}

function rupeesFromPaise(p) {
  const n = Number(p) || 0;
  return `₹${(n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

/** One-line label for admin tables (full URL in `title` / copy). */
function shortReportUrlLabel(raw) {
  const u = String(raw || '').trim();
  if (!u) return '—';
  try {
    const x = new URL(u);
    const tail = `${x.pathname || ''}${x.search || ''}` || x.hostname;
    return tail.length > 72 ? `${tail.slice(0, 70)}…` : tail;
  } catch {
    return u.length > 72 ? `${u.slice(0, 70)}…` : u;
  }
}

function sinceDaysIso(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 19).replace('T', ' ');
}

/** DB `created_at` comparisons (UTC / server-style string). */
function msToSqlUtc(ms) {
  return new Date(ms).toISOString().slice(0, 19).replace('T', ' ');
}

/** Bind `<input type="datetime-local" />` to millisecond timestamps. */
function toDatetimeLocalValue(ms) {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function parseDatetimeLocalToMs(s) {
  const t = new Date(s).getTime();
  return Number.isNaN(t) ? null : t;
}

function adminEventsQuery({ limit = 200, offset = 0, eventName, since, until, allTime }) {
  const p = new URLSearchParams();
  p.set('limit', String(limit));
  if (offset) p.set('offset', String(offset));
  if (!allTime) {
    if (since) p.set('since', since);
    if (until) p.set('until', until);
  }
  const n = eventName != null ? String(eventName).trim() : '';
  if (n) p.set('eventName', n);
  return `?${p.toString()}`;
}

const ROLE_THEME = {
  CTO: {
    inner: 'relative z-10 mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10',
    heading: 'text-slate-900',
    sub: 'text-slate-600',
    card: 'rounded-2xl border border-indigo-200/70 bg-white p-4 shadow-md',
    cardHover: 'hover:border-indigo-400/50 hover:shadow-indigo-100/60',
    panel: 'rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm shadow-slate-200/40',
    foot: 'text-slate-500',
    accent: '#6366f1',
    accent2: '#0ea5e9',
    barStudents: '#6366f1',
    barEvents: '#818cf8',
    /** Primary CTA — filled gradient */
    btnGradient:
      'border-0 bg-gradient-to-br from-indigo-500 via-violet-500 to-sky-500 text-white shadow-lg shadow-indigo-900/25 transition hover:brightness-105 hover:shadow-indigo-900/35 active:scale-[0.99] disabled:pointer-events-none disabled:opacity-40 disabled:shadow-none',
    /** Secondary — soft rim + light wash matching the role */
    btnGradientOutline:
      'border border-indigo-200/90 bg-gradient-to-br from-white via-indigo-50/80 to-sky-50/70 text-indigo-950 shadow-sm transition hover:border-indigo-300 hover:from-indigo-50/90 hover:via-violet-50/80 hover:to-sky-50/80 active:scale-[0.99]',
  },
  CEO: {
    inner: 'relative z-10 mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10',
    heading: 'text-slate-900',
    sub: 'text-slate-600',
    card: 'rounded-2xl border border-amber-200/90 bg-white/95 p-4 shadow-md backdrop-blur-sm',
    cardHover: 'hover:border-amber-400/80 hover:shadow-amber-200/40',
    panel: 'rounded-2xl border border-amber-100 bg-white/90 p-4 shadow-sm',
    foot: 'text-slate-500',
    accent: '#d97706',
    accent2: '#0369a1',
    barStudents: '#d97706',
    barEvents: '#0284c7',
    btnGradient:
      'border-0 bg-gradient-to-br from-amber-500 via-orange-500 to-sky-600 text-white shadow-lg shadow-amber-900/20 transition hover:brightness-105 hover:shadow-amber-900/30 active:scale-[0.99] disabled:pointer-events-none disabled:opacity-40 disabled:shadow-none',
    btnGradientOutline:
      'border border-amber-200/90 bg-gradient-to-br from-white via-amber-50/90 to-sky-50/70 text-amber-950 shadow-sm transition hover:border-amber-300 hover:from-amber-50 hover:via-orange-50/80 hover:to-sky-50 active:scale-[0.99]',
  },
  CMO: {
    inner: 'relative z-10 mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10',
    heading: 'text-violet-950',
    sub: 'text-violet-800/80',
    card: 'rounded-2xl border border-fuchsia-200/90 bg-white/95 p-4 shadow-md backdrop-blur-sm',
    cardHover: 'hover:border-fuchsia-400/70 hover:shadow-fuchsia-200/40',
    panel: 'rounded-2xl border border-violet-100 bg-white/90 p-4 shadow-sm',
    foot: 'text-slate-500',
    accent: '#c026d3',
    accent2: '#7c3aed',
    barStudents: '#c026d3',
    barEvents: '#7c3aed',
    btnGradient:
      'border-0 bg-gradient-to-br from-fuchsia-500 via-purple-600 to-violet-600 text-white shadow-lg shadow-fuchsia-900/25 transition hover:brightness-105 hover:shadow-purple-900/30 active:scale-[0.99] disabled:pointer-events-none disabled:opacity-40 disabled:shadow-none',
    btnGradientOutline:
      'border border-fuchsia-200/90 bg-gradient-to-br from-white via-fuchsia-50/80 to-violet-50/80 text-violet-950 shadow-sm transition hover:border-fuchsia-300 hover:from-fuchsia-50 hover:to-violet-50 active:scale-[0.99]',
  },
  COO: {
    inner: 'relative z-10 mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10',
    heading: 'text-rose-950',
    sub: 'text-rose-900/70',
    card: 'rounded-2xl border border-rose-100 bg-gradient-to-br from-white via-rose-50/90 to-pink-50/80 p-4 shadow-md',
    cardHover: 'hover:border-rose-300/80 hover:shadow-rose-200/50',
    panel: 'rounded-2xl border border-rose-100 bg-white/95 p-4 shadow-sm',
    foot: 'text-rose-600/80',
    accent: '#e11d48',
    accent2: '#fb7185',
    barStudents: '#fb7185',
    barEvents: '#f43f5e',
    btnGradient:
      'border-0 bg-gradient-to-br from-rose-500 via-pink-500 to-rose-600 text-white shadow-lg shadow-rose-900/25 transition hover:brightness-105 hover:shadow-rose-900/35 active:scale-[0.99] disabled:pointer-events-none disabled:opacity-40 disabled:shadow-none',
    btnGradientOutline:
      'border border-rose-200/90 bg-gradient-to-br from-white via-rose-50/90 to-pink-50/80 text-rose-950 shadow-sm transition hover:border-rose-300 hover:from-rose-50 hover:to-pink-50 active:scale-[0.99]',
  },
};

const ROLE_BLURB = {
  CTO: 'Systems, data quality, incidents, and full event stream.',
  CEO: 'Growth, revenue, funnel health, and partnerships.',
  CMO: 'Campaigns, UTM performance, attribution, and voucher campaigns.',
  COO: 'Day-to-day volume, throughput, and school operations.',
};

function adminFetchFailedHint(path, err) {
  const base = apiBase();
  const target = `${base || (typeof window !== 'undefined' ? window.location.origin : '')}${path}`;
  return formatApiFetchError(target, err);
}

async function adminGet(path) {
  let res;
  try {
    res = await fetch(`${apiBase()}${path}`, { credentials: 'include' });
  } catch (e) {
    throw new Error(adminFetchFailedHint(path, e));
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

async function adminPost(path, body) {
  let res;
  try {
    res = await fetch(`${apiBase()}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    });
  } catch (e) {
    throw new Error(adminFetchFailedHint(path, e));
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

async function adminDelete(path) {
  let res;
  try {
    res = await fetch(`${apiBase()}${path}`, { method: 'DELETE', credentials: 'include' });
  } catch (e) {
    throw new Error(adminFetchFailedHint(path, e));
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

async function fetchDashboard() {
  return adminGet('/api/admin/dashboard');
}

async function postAdminLogin(secret) {
  const url = `${apiBase()}/api/admin/login`;
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ adminSecret: normalizeKey(secret) }),
    });
  } catch (e) {
    throw new Error(formatApiFetchError(url, e));
  }
  const data = await res.json().catch(() => ({}));
  return { res, data };
}

async function postAdminLogout() {
  const url = `${apiBase()}/api/admin/logout`;
  try {
    await fetch(url, { method: 'POST', credentials: 'include' });
  } catch {
    /* ignore */
  }
}

async function probeAdminSession() {
  const url = `${apiBase()}/api/admin/session`;
  try {
    const res = await fetch(url, { credentials: 'include' });
    return res.ok;
  } catch {
    return false;
  }
}

function MetricCard({ label, value, hint, theme, onClick, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`text-left w-full ${theme.card} ${theme.cardHover} transition ${disabled ? 'opacity-60 cursor-default' : 'cursor-pointer'}`}
    >
      <p className={`text-[11px] font-semibold uppercase tracking-[0.12em] ${theme.sub}`}>{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${theme.heading}`}>{value}</p>
      {hint ? <p className={`mt-1 text-xs ${theme.sub}`}>{hint}</p> : null}
      {!disabled ? <p className={`mt-2 text-[10px] font-medium ${theme.foot || 'text-slate-500'}`}>Click for details →</p> : null}
    </button>
  );
}

function DetailModal({ open, title, loading, error, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center p-0 sm:p-4" role="dialog" aria-modal="true">
      <button type="button" className="absolute inset-0 bg-black/50" aria-label="Close overlay" onClick={onClose} />
      <div className="relative z-10 flex max-h-[88vh] w-full max-w-4xl flex-col rounded-t-2xl border border-slate-200 bg-white shadow-2xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h2 className="pr-6 text-sm font-semibold text-slate-900">{title}</h2>
          <button type="button" className="rounded-full p-1 text-slate-500 hover:bg-slate-100" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : null}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
          {!loading && !error ? children : null}
        </div>
      </div>
    </div>
  );
}

function JsonCell({ value }) {
  const [open, setOpen] = useState(false);
  const str = useMemo(() => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }, [value]);
  if (!str) return <span className="text-slate-400">—</span>;
  if (str.length < 80) return <span className="font-mono text-[11px] text-slate-700">{str}</span>;
  return (
    <div>
      <button type="button" className="text-xs font-medium text-indigo-600 hover:underline" onClick={() => setOpen((o) => !o)}>
        {open ? 'Hide' : 'Show'} JSON
      </button>
      {open ? <pre className="mt-1 max-h-40 overflow-auto rounded bg-slate-50 p-2 text-[10px] text-slate-800">{str}</pre> : null}
    </div>
  );
}

function ExecutiveShellWordmark() {
  const [markSrc, setMarkSrc] = useState(getBrandLogoSrc);
  return (
    <div className="relative shrink-0">
      <div
        className="absolute -inset-1 rounded-full bg-gradient-to-br from-blue-500/25 via-sky-400/20 to-teal-500/25 blur-md"
        aria-hidden
      />
      <img
        src={markSrc}
        alt=""
        width={56}
        height={56}
        decoding="async"
        className="relative h-12 w-12 rounded-full object-contain shadow-lg shadow-blue-900/15 ring-2 ring-white/90 sm:h-14 sm:w-14"
        onError={() => setMarkSrc((s) => (s === getBrandFallbackMarkSrc() ? s : getBrandFallbackMarkSrc()))}
      />
    </div>
  );
}

export default function AdminRoleDashboardPage() {
  /** True when HttpOnly admin session cookie is accepted by the API (never readable from JS). */
  const [adminAuthed, setAdminAuthed] = useState(false);
  const [sessionChecking, setSessionChecking] = useState(true);
  /** One-time paste of the server secret; cleared after successful exchange. */
  const [secretDraft, setSecretDraft] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [role, setRole] = useState('');
  const [dash, setDash] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState({ open: false, title: '', path: '', extra: '' });
  const [detailRows, setDetailRows] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState('');

  const [orgName, setOrgName] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [orgSeat, setOrgSeat] = useState('5000');
  const [orgPilot, setOrgPilot] = useState('');
  /** When true, new students on this school link are marked paid for report access (org bills you; parents do not pay per seat). */
  const [orgCoversStudents, setOrgCoversStudents] = useState(false);
  const [vCode, setVCode] = useState('');
  const [vPct, setVPct] = useState('10');
  const [vMax, setVMax] = useState('0');
  const [vLabel, setVLabel] = useState('');
  const [opsSchoolBusy, setOpsSchoolBusy] = useState(false);
  const [opsVoucherBusy, setOpsVoucherBusy] = useState(false);
  const [opsMsg, setOpsMsg] = useState('');
  const [parentResendStudentId, setParentResendStudentId] = useState('');
  const [parentResendBusy, setParentResendBusy] = useState(false);

  const [eventFilter, setEventFilter] = useState('');
  const eventFilterRef = useRef('');
  useEffect(() => {
    eventFilterRef.current = eventFilter;
  }, [eventFilter]);

  const prevRoleRef = useRef(role);
  const [ctoEvents, setCtoEvents] = useState([]);
  const [ctoEventsLoading, setCtoEventsLoading] = useState(false);
  const [ctoEventsErr, setCtoEventsErr] = useState('');
  const [ctoEventsAllTime, setCtoEventsAllTime] = useState(false);
  const [ctoRangeStartMs, setCtoRangeStartMs] = useState(() => Date.now() - 7 * 86400000);
  const [ctoRangeEndMs, setCtoRangeEndMs] = useState(() => Date.now());

  useEffect(() => {
    if (role === 'CTO' && prevRoleRef.current !== 'CTO') {
      setCtoEventsAllTime(false);
      setCtoRangeStartMs(Date.now() - 7 * 86400000);
      setCtoRangeEndMs(Date.now());
      setEventFilter('');
      eventFilterRef.current = '';
    }
    prevRoleRef.current = role;
  }, [role]);

  const theme = ROLE_THEME[role] || ROLE_THEME.CEO;

  const load = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      setDash(await fetchDashboard());
    } catch (e) {
      setDash(null);
      const msg = String(e.message || e);
      if (/unauthorized|\(401\)/i.test(msg)) {
        await postAdminLogout();
        setAdminAuthed(false);
        setRole('');
        setErr('Admin session expired or was revoked. Sign in again with your server secret.');
        return;
      }
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ok = await probeAdminSession();
        if (!cancelled && ok) setAdminAuthed(true);
      } finally {
        if (!cancelled) setSessionChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (role && adminAuthed) void load();
  }, [role, adminAuthed, load]);

  useEffect(() => {
    setOpsMsg('');
  }, [role]);

  const onVerifyAdmin = async (e) => {
    e.preventDefault();
    setErr('');
    const raw = normalizeKey(secretDraft);
    if (!raw) return;
    setAuthBusy(true);
    try {
      const { res, data } = await postAdminLogin(raw);
      if (res.ok && data?.ok) {
        setAdminAuthed(true);
        setSecretDraft('');
        return;
      }
      throw new Error(data?.error || `Sign-in failed (${res.status})`);
    } catch (err) {
      setErr(String(err.message || err));
    } finally {
      setAuthBusy(false);
    }
  };

  useEffect(() => {
    if (!opsMsg) return undefined;
    const t = window.setTimeout(() => setOpsMsg(''), 8000);
    return () => window.clearTimeout(t);
  }, [opsMsg]);

  const totals = dash?.totals || {};
  const chartData = useMemo(() => {
    const rows = dash?.charts?.dailyNewStudents || [];
    return rows.map((r) => ({
      day: String(r.d || '').slice(5),
      students: Number(r.c) || 0,
    }));
  }, [dash]);

  const payChartData = useMemo(() => {
    const rows = dash?.charts?.dailyCapturedPayments || [];
    return rows.map((r) => ({
      day: String(r.d || '').slice(5),
      count: Number(r.c) || 0,
      rev: Math.round((Number(r.rev) || 0) / 100),
    }));
  }, [dash]);

  const funnelData = useMemo(() => {
    const rows = dash?.funnelEventsLast30Days || [];
    return rows.map((r) => ({ name: r.event_name, count: Number(r.c) || 0 }));
  }, [dash]);

  function listKeyForAdminPath(path) {
    if (path.includes('/students')) return 'students';
    if (path.includes('/events')) return 'events';
    if (path.includes('/voucher-redemptions')) return 'redemptions';
    if (path.includes('/payments')) return 'payments';
    if (path.includes('/assessment-sessions')) return 'sessions';
    return null;
  }

  const openDetail = async (title, path) => {
    setDetail({ open: true, title, path, extra: '' });
    setDetailRows(null);
    setDetailErr('');
    setDetailLoading(true);
    try {
      const data = await adminGet(path);
      const key = listKeyForAdminPath(path) || Object.keys(data).find((k) => Array.isArray(data[k]));
      setDetailRows(key ? data[key] : []);
    } catch (e) {
      setDetailErr(String(e.message || e));
      setDetailRows(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetail({ open: false, title: '', path: '', extra: '' });
    setDetailRows(null);
  };

  const ctoEventQueryBody = useCallback(() => {
    let startMs = ctoRangeStartMs;
    let endMs = ctoRangeEndMs;
    if (!ctoEventsAllTime && startMs > endMs) {
      const t = startMs;
      startMs = endMs;
      endMs = t;
    }
    return {
      allTime: ctoEventsAllTime,
      since: ctoEventsAllTime ? '' : msToSqlUtc(startMs),
      until: ctoEventsAllTime ? '' : msToSqlUtc(endMs),
    };
  }, [ctoEventsAllTime, ctoRangeStartMs, ctoRangeEndMs]);

  const fetchCtoEvents = useCallback(
    async (opts = {}) => {
      if (!adminAuthed) return;
      const nameQ = opts.eventName !== undefined ? String(opts.eventName) : eventFilterRef.current;
      if (opts.eventName !== undefined) {
        eventFilterRef.current = nameQ;
        setEventFilter(nameQ);
      }
      const body = ctoEventQueryBody();
      setCtoEventsLoading(true);
      setCtoEventsErr('');
      try {
        const path = `/api/admin/events${adminEventsQuery({
          limit: opts.limit ?? 250,
          offset: opts.offset ?? 0,
          eventName: nameQ,
          since: body.since,
          until: body.until,
          allTime: body.allTime,
        })}`;
        const data = await adminGet(path);
        setCtoEvents(Array.isArray(data.events) ? data.events : []);
      } catch (e) {
        setCtoEventsErr(String(e.message || e));
        setCtoEvents([]);
      } finally {
        setCtoEventsLoading(false);
      }
    },
    [adminAuthed, ctoEventQueryBody],
  );

  useEffect(() => {
    if (role !== 'CTO' || !adminAuthed) return undefined;
    void fetchCtoEvents();
    return undefined;
  }, [role, adminAuthed, ctoEventsAllTime, ctoRangeStartMs, ctoRangeEndMs, fetchCtoEvents]);

  const reloadEventsForCto = async () => {
    const body = ctoEventQueryBody();
    const name = eventFilterRef.current.trim();
    const path = `/api/admin/events${adminEventsQuery({
      limit: 800,
      offset: 0,
      eventName: name,
      since: body.since,
      until: body.until,
      allTime: body.allTime,
    })}`;
    const rangeLabel = body.allTime ? 'all time' : `${body.since} → ${body.until} (UTC)`;
    await openDetail(`Events — ${rangeLabel}${name ? ` · name contains "${name}"` : ''}`, path);
  };

  const ctoEventSummary = useMemo(() => {
    const map = new Map();
    for (const ev of ctoEvents) {
      const k = ev.event_name || '(none)';
      map.set(k, (map.get(k) || 0) + 1);
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 24);
  }, [ctoEvents]);

  const onCreateOrg = async (e) => {
    e.preventDefault();
    setOpsMsg('');
    setOpsSchoolBusy(true);
    try {
      const { id } = await adminPost('/api/admin/organizations', {
        name: orgName.trim(),
        slug: orgSlug.trim(),
        type: 'school',
        coversStudentReports: orgCoversStudents,
      });
      const cap = Math.max(1, Math.min(1_000_000, Number(orgSeat) || 5000));
      if (id) {
        await adminPost('/api/admin/org-subscriptions', {
          organizationId: id,
          seatCap: cap,
          pilotStarterPricePaise: orgPilot.trim() ? Number(orgPilot) : null,
        });
      }
      setOpsMsg(
        `School created (id ${id?.slice(0, 8)}…). Public URL: /school/${orgSlug.trim().toLowerCase()}${orgCoversStudents ? ' · Org covers student reports (no parent pay per report).' : ''}`,
      );
      setOrgName('');
      setOrgSlug('');
      setOrgCoversStudents(false);
      await load();
    } catch (x) {
      setOpsMsg(String(x.message || x));
    } finally {
      setOpsSchoolBusy(false);
    }
  };

  const onCreateVoucher = async (e) => {
    e.preventDefault();
    setOpsMsg('');
    setOpsVoucherBusy(true);
    try {
      await adminPost('/api/admin/vouchers', {
        code: vCode.trim(),
        percentOff: Number(vPct) || 0,
        maxUses: Number(vMax) || 0,
        label: vLabel.trim(),
      });
      setOpsMsg('Voucher created.');
      setVCode('');
      setVLabel('');
      await load();
    } catch (x) {
      setOpsMsg(String(x.message || x));
    } finally {
      setOpsVoucherBusy(false);
    }
  };

  const onDeleteVoucher = async (id) => {
    if (!id || !window.confirm('Deactivate this voucher?')) return;
    setOpsMsg('');
    setOpsVoucherBusy(true);
    try {
      await adminDelete(`/api/admin/vouchers/${encodeURIComponent(id)}`);
      setOpsMsg('Voucher deactivated.');
      await load();
    } catch (x) {
      setOpsMsg(String(x.message || x));
    } finally {
      setOpsVoucherBusy(false);
    }
  };

  const onResendParentSchoolSms = async (e) => {
    e.preventDefault();
    setOpsMsg('');
    setParentResendBusy(true);
    try {
      const data = await adminPost('/api/admin/school-parent/resend-sms', {
        studentId: parentResendStudentId.trim(),
      });
      const ok = data?.parentNotify?.ok !== false;
      setOpsMsg(
        ok
          ? 'Parent payment link resent (WhatsApp/SMS per server config). Magic-link expiry refreshed — see API PARENT_MAGIC_LINK_TTL_HOURS.'
          : `Resend finished but delivery may have failed (mode: ${data?.parentNotify?.mode || 'unknown'}).`,
      );
    } catch (x) {
      setOpsMsg(String(x.message || x));
    } finally {
      setParentResendBusy(false);
    }
  };

  const showStudentsChart = role === 'CTO' || role === 'CEO' || role === 'CMO';
  const showPayChart = role === 'CTO' || role === 'CEO' || role === 'CMO';
  const showFunnelChart = role === 'CTO' || role === 'CEO' || role === 'CMO';
  const showUtm = role === 'CTO' || role === 'CMO' || role === 'CEO';
  const showCooDaily = role === 'COO';

  const metrics = useMemo(() => {
    const all = [
      { id: 'stu', label: 'Total students', value: totals.totalStudents, path: '/api/admin/students?limit=800', hint: 'All profiles' },
      { id: 'stu30', label: 'New students (30d)', value: totals.studentsLast30Days, path: `/api/admin/students?limit=800&since=${encodeURIComponent(sinceDaysIso(30))}`, hint: 'Last 30 days' },
      { id: 'start', label: 'Assessments started', value: totals.assessmentsStarted, path: '/api/admin/assessment-sessions?limit=400', hint: 'Sessions with progress' },
      { id: 'done', label: 'Assessments completed', value: totals.assessmentsCompleted, path: '/api/admin/assessment-sessions?limit=400&status=assessment_done', hint: 'Status = assessment_done' },
      { id: 'pay', label: 'Captured payments', value: totals.successfulPayments, path: '/api/admin/payments?limit=200', hint: 'Razorpay captured' },
      { id: 'rev', label: 'Revenue (all time)', value: rupeesFromPaise(totals.totalRevenuePaise), path: '/api/admin/payments?limit=200', hint: 'Same list as payments' },
      { id: 'rev30', label: 'Revenue (30d)', value: rupeesFromPaise(totals.revenueLast30DaysPaise), path: '/api/admin/payments?limit=200', hint: 'Filter in sheet by date' },
      { id: 'vusr', label: 'Students w/ voucher use', value: totals.studentsWithVoucherRedemption, path: '/api/admin/voucher-redemptions?limit=500', hint: 'Redemption rows' },
      {
        id: 'pdfp',
        label: 'Parent link PDF downloads',
        value: Number(totals.parentMagicLinkPdfDownloads ?? 0),
        path: '/api/admin/assessment-sessions?limit=500',
        hint: 'Parent magic-link report downloads',
      },
      {
        id: 'pdfall',
        label: 'Total PDF downloads',
        value: Number(totals.totalPdfDownloads ?? 0),
        path: '/api/admin/assessment-sessions?limit=500',
        hint: 'All download surfaces',
      },
    ];
    if (role === 'COO') return all.filter((m) => ['stu', 'start', 'done', 'pay', 'stu30', 'pdfp', 'pdfall'].includes(m.id));
    if (role === 'CMO') return all.filter((m) => ['stu30', 'stu', 'vusr', 'start', 'done', 'pdfp', 'pdfall'].includes(m.id));
    if (role === 'CEO') return all.filter((m) => ['stu', 'stu30', 'rev', 'rev30', 'pay', 'vusr', 'start', 'done', 'pdfp', 'pdfall'].includes(m.id));
    return all;
  }, [role, totals]);

  if (!role) {
    if (sessionChecking) {
      return (
        <div className="relative min-h-[100dvh] overflow-x-hidden text-slate-900">
          <Helmet>
            <title>{`Leadership · ${BRAND_NAME}`}</title>
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-sky-50 via-blue-50 to-teal-50" aria-hidden />
          <div className="relative z-10 mx-auto flex max-w-lg flex-col items-center px-4 py-24">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" aria-hidden />
            <p className="mt-4 text-sm text-slate-600">Checking session…</p>
          </div>
        </div>
      );
    }

    const needSecret = !adminAuthed;
    return (
      <div className="relative min-h-[100dvh] overflow-x-hidden text-slate-900">
        <Helmet>
          <title>{`Leadership · ${BRAND_NAME}`}</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-sky-50 via-blue-50 to-teal-50" aria-hidden />
        <div className="relative z-10 mx-auto max-w-lg px-4 py-14 sm:py-20">
          <BrandFormChrome wordmarkSubtitle="Leadership · metrics, schools & growth" />
          <div className="rounded-2xl border border-blue-100/90 bg-white/95 p-6 shadow-xl shadow-blue-900/10 backdrop-blur-sm sm:p-8">
            <div className="mb-6 flex items-start gap-3 rounded-xl border border-sky-100 bg-sky-50/80 px-4 py-3 text-sm text-sky-900">
              <Shield className="h-5 w-5 shrink-0 text-teal-600" aria-hidden />
              <p>
                Private executive dashboard. Sign in with the server admin secret once; the API sets an HttpOnly session
                cookie (JavaScript cannot read it). This URL is not linked from the public site.
              </p>
            </div>
            <h1 className="font-display text-xl font-semibold text-slate-900 sm:text-2xl">Sign in</h1>
            {needSecret ? (
              <>
                <p className="mt-2 text-sm text-slate-600">
                  Paste the long-lived admin secret from your API host environment (same value you configure on the server,
                  never committed to git). It is sent once over HTTPS; the server responds with an HttpOnly cookie — not a
                  token visible in the browser or on every request header.
                </p>
                <form onSubmit={onVerifyAdmin} className="mt-6 space-y-3">
                  <label htmlFor="admin-exec-secret" className="block text-xs font-semibold uppercase tracking-wide text-slate-600">
                    Admin secret (one-time)
                  </label>
                  <input
                    id="admin-exec-secret"
                    type="password"
                    autoComplete="off"
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-inner outline-none ring-blue-500/30 transition focus:border-blue-400 focus:ring-2"
                    value={secretDraft}
                    onChange={(e) => setSecretDraft(e.target.value)}
                    placeholder="Paste server admin secret"
                  />
                  {err ? <p className="text-sm text-red-700">{err}</p> : null}
                  <Button type="submit" disabled={authBusy || !normalizeKey(secretDraft)} className="w-full rounded-xl font-semibold">
                    {authBusy ? (
                      <>
                        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                        Verifying…
                      </>
                    ) : (
                      'Verify and continue'
                    )}
                  </Button>
                </form>
              </>
            ) : (
              <>
                <p className="mt-2 text-sm text-slate-600">
                  Session established (cookie on this browser until you sign out or it expires). Pick a role — or sign out
                  to clear it.
                </p>
                {err ? <p className="mt-3 text-sm text-red-700">{err}</p> : null}
                <div className="mt-6 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    className="rounded-xl border border-slate-200 font-semibold"
                    onClick={() => {
                      void postAdminLogout().then(() => {
                        setAdminAuthed(false);
                        setSecretDraft('');
                        setErr('');
                      });
                    }}
                  >
                    Sign out
                  </Button>
                </div>
                <p className="mt-6 text-xs font-semibold uppercase tracking-wide text-slate-500">Choose your view</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {ROLES.map((r) => (
                    <Button
                      key={r}
                      type="button"
                      variant="secondary"
                      className={cn(
                        (ROLE_THEME[r] || ROLE_THEME.CEO).btnGradient,
                        'h-auto min-h-[3.25rem] flex-col gap-0.5 rounded-xl py-3 text-sm font-semibold [&_span:last-child]:text-white/85',
                      )}
                      onClick={() => setRole(r)}
                    >
                      <span className="font-semibold tracking-wide">{r}</span>
                      <span className="text-[11px] font-medium opacity-95">{ROLE_LEAD_NAME[r]}</span>
                    </Button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-[100dvh] overflow-x-hidden text-slate-900">
      <Helmet>
        <title>{`${ROLE_LEAD_NAME[role]} · ${role} · ${BRAND_NAME}`}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-sky-50 via-blue-50 to-teal-50" aria-hidden />
      <div className={theme.inner}>
        <header className="mb-8 flex flex-col gap-4 border-b border-slate-200/80 pb-6 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center">
            <ExecutiveShellWordmark />
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-teal-700">{BRAND_NAME}</p>
              <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">{ROLE_LEAD_NAME[role]}</span>
                <span className={`ml-2 text-xl font-semibold sm:text-2xl ${theme.heading}`}>· {role}</span>
              </h1>
              <p className="mt-1 max-w-2xl text-sm text-slate-600">{ROLE_BLURB[role]}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className={cn(theme.btnGradient, 'rounded-full px-4 font-semibold shadow-md')}
              onClick={() => void load()}
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh data'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className={cn(theme.btnGradientOutline, 'rounded-full px-4 font-semibold')}
              onClick={() => setRole('')}
            >
              <LogOut className="mr-1 h-4 w-4" />
              Change role
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="rounded-full border border-slate-200 px-4 font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => {
                void postAdminLogout().then(() => {
                  setAdminAuthed(false);
                  setRole('');
                  setDash(null);
                  setErr('');
                });
              }}
            >
              Sign out
            </Button>
          </div>
        </header>

        {err ? <p className="mb-4 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</p> : null}
        {opsMsg ? (
          <div
            className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-emerald-200/90 bg-emerald-50/95 px-3 py-2.5 text-sm text-emerald-950 shadow-sm"
            role="status"
          >
            <p className="min-w-0 flex-1 select-text leading-snug">{opsMsg}</p>
            <button
              type="button"
              className="shrink-0 rounded-lg p-1 text-emerald-800 hover:bg-emerald-100/80"
              aria-label="Dismiss"
              onClick={() => setOpsMsg('')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : null}

        {/* Partner school + discount voucher — same row on md+ for all roles */}
        <div className="mb-6 grid grid-cols-1 items-start gap-4 md:grid-cols-2">
          <section className={`min-w-0 ${theme.panel}`}>
            <h2 className={`mb-1 flex items-center gap-2 text-sm font-semibold ${theme.heading}`}>
              <Building2 className="h-4 w-4 shrink-0 text-indigo-600" aria-hidden />
              Partner school &amp; plan
            </h2>
            <p className={`mb-3 text-[11px] leading-relaxed ${theme.sub}`}>
              Name + URL slug + subscription (seat cap, optional pilot Starter price in paise for this school&apos;s checkout link).
            </p>
            <form className="grid gap-2.5" onSubmit={onCreateOrg}>
              <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" placeholder="School display name" value={orgName} onChange={(e) => setOrgName(e.target.value)} required />
              <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" placeholder="URL slug (e.g. delhi-public)" value={orgSlug} onChange={(e) => setOrgSlug(e.target.value)} required />
              <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" type="number" min={1} placeholder="Seat cap" value={orgSeat} onChange={(e) => setOrgSeat(e.target.value)} />
              <div>
                <input
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                  type="number"
                  placeholder="Pilot Starter price (paise, optional)"
                  value={orgPilot}
                  onChange={(e) => setOrgPilot(e.target.value)}
                />
                <p className={`mt-1 text-[10px] leading-relaxed ${theme.sub}`}>Optional · ₹199 → 19900 paise</p>
              </div>
              <label className={`flex cursor-pointer items-center gap-2 text-xs ${theme.sub}`}>
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300"
                  checked={orgCoversStudents}
                  onChange={(e) => setOrgCoversStudents(e.target.checked)}
                />
                <span>School / org already paid — students can print full report after assessment (no parent payment per student)</span>
              </label>
              <Button type="submit" size="sm" disabled={opsSchoolBusy} className="w-fit rounded-xl">
                {opsSchoolBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create school & activate plan'}
              </Button>
            </form>
          </section>

          <section className={`min-w-0 ${theme.panel}`}>
            <h2 className={`mb-1 flex items-center gap-2 text-sm font-semibold ${theme.heading}`}>
              <Ticket className="h-4 w-4 shrink-0 text-violet-600" aria-hidden />
              Discount voucher
            </h2>
            <p className={`mb-3 text-[11px] leading-relaxed ${theme.sub}`}>
              Promo codes at checkout. Separate from the school record — each form is independent.
            </p>
            <form className="grid gap-2.5" onSubmit={onCreateVoucher}>
              <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" placeholder="Code (e.g. SUMMER15)" value={vCode} onChange={(e) => setVCode(e.target.value)} required />
              <div className="grid grid-cols-2 gap-2">
                <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" type="number" min={0} max={100} placeholder="% off" value={vPct} onChange={(e) => setVPct(e.target.value)} />
                <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" type="number" min={0} placeholder="Max uses (0 = ∞)" value={vMax} onChange={(e) => setVMax(e.target.value)} />
              </div>
              <input className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" placeholder="Label (internal)" value={vLabel} onChange={(e) => setVLabel(e.target.value)} />
              <Button type="submit" size="sm" disabled={opsVoucherBusy} className="w-fit rounded-xl">
                {opsVoucherBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create voucher'}
              </Button>
            </form>
          </section>
        </div>

        {(role === 'COO' || role === 'CTO') && (
          <section className={`mb-6 ${theme.panel}`}>
            <h2 className={`mb-1 flex items-center gap-2 text-sm font-semibold ${theme.heading}`}>
              School parent link (ops)
            </h2>
            <p className={`mb-3 text-[11px] leading-relaxed ${theme.sub}`}>
              If the parent did not receive the WhatsApp/SMS payment link, paste the student&apos;s UUID (from the
              students or sessions export) and resend. Only <strong>parent-pay</strong> school students with a stored
              parent phone are eligible.
            </p>
            <form className="flex flex-col gap-2 sm:flex-row sm:items-end" onSubmit={onResendParentSchoolSms}>
              <input
                className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-900"
                placeholder="Student UUID (e.g. from /api/admin/students)"
                value={parentResendStudentId}
                onChange={(e) => setParentResendStudentId(e.target.value)}
                autoComplete="off"
              />
              <Button type="submit" size="sm" variant="secondary" className="w-full shrink-0 rounded-xl sm:w-auto" disabled={parentResendBusy}>
                {parentResendBusy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    Sending…
                  </>
                ) : (
                  'Resend parent payment link'
                )}
              </Button>
            </form>
          </section>
        )}

        {loading && !dash ? (
          <div className={`mb-4 flex items-center gap-2 text-sm ${theme.sub}`}>
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading dashboard…
          </div>
        ) : null}

        {dash ? (
          <>
            <section className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {metrics.map((m) => (
                <MetricCard
                  key={m.id}
                  label={m.label}
                  value={m.value}
                  hint={m.hint}
                  theme={theme}
                  onClick={() => void openDetail(m.label, m.path)}
                />
              ))}
            </section>

            {showStudentsChart && !showCooDaily && (
              <section className={`mb-8 grid gap-6 lg:grid-cols-2`}>
                <div className={theme.panel}>
                  <h2 className={`mb-2 text-sm font-semibold ${theme.heading}`}>New students / day (IST dates in DB)</h2>
                  <p className={`mb-2 text-xs ${theme.sub}`}>Chart buckets use server `DATE(created_at)`; hover shows UTC day key — see list views for IST-formatted rows.</p>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="students" fill={theme.barStudents} radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                {showFunnelChart ? (
                  <div className={theme.panel}>
                    <h2 className={`mb-2 text-sm font-semibold ${theme.heading}`}>Product events (30d)</h2>
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={funnelData} layout="vertical" margin={{ left: 4, right: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Bar dataKey="count" fill={theme.barEvents} radius={[0, 6, 6, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : null}
              </section>
            )}

            {showPayChart && (
              <section className={`mb-8 grid gap-4 md:grid-cols-2 ${theme.panel}`}>
                <div>
                  <h3 className={`mb-2 text-xs font-semibold ${theme.heading}`}>Payments / day (count)</h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={payChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill={theme.accent} radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div>
                  <h3 className={`mb-2 text-xs font-semibold ${theme.heading}`}>Revenue / day (₹, from paise)</h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={payChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Bar dataKey="rev" fill={theme.accent2} radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </section>
            )}

            {showCooDaily && (
              <section className={`mb-8 ${theme.panel}`}>
                <h2 className={`mb-2 text-sm font-semibold ${theme.heading}`}>Daily sign-ups (30d)</h2>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#fce7f3" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="students" fill={theme.barStudents} radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>
            )}

            {showUtm && (
              <section className={`mb-8 ${theme.panel}`}>
                <h2 className={`mb-2 text-sm font-semibold ${theme.heading}`}>UTM / acquisition source</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className={`border-b text-xs uppercase ${theme.sub}`}>
                        <th className="py-2 pr-4">Source</th>
                        <th className="py-2">Students</th>
                        <th className="py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {(dash.utmSourceBreakdown || []).map((r) => (
                        <tr key={String(r.utm_source)} className="border-b border-slate-100">
                          <td className="py-2 pr-4">{r.utm_source}</td>
                          <td className="py-2">{r.c}</td>
                          <td className="py-2 text-right">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-xs"
                              onClick={() =>
                                void openDetail(
                                  `Students — source: ${r.utm_source}`,
                                  `/api/admin/students?limit=500&utmSource=${encodeURIComponent(String(r.utm_source === '(none)' ? '' : r.utm_source))}`,
                                )
                              }
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            <section className={`mb-8 grid gap-6 lg:grid-cols-2 ${theme.panel}`}>
              <div>
                <h2 className={`mb-2 text-sm font-semibold ${theme.heading}`}>Organizations</h2>
                <div className="max-h-72 space-y-2 overflow-auto text-sm">
                  {(dash.organizationUsage || []).map((o) => (
                    <div key={o.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-white/60 px-2 py-2">
                      <div>
                        <div className="font-medium text-slate-900">{o.name}</div>
                        <div className="text-xs text-slate-500">/{o.slug}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-slate-600">{o.student_count} students</span>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" title="Copy slug" onClick={() => void navigator.clipboard.writeText(o.slug)}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() =>
                            void openDetail(`Students — ${o.name}`, `/api/admin/students?limit=500&organizationId=${encodeURIComponent(o.id)}`)
                          }
                        >
                          Students
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h2 className={`mb-2 text-sm font-semibold ${theme.heading}`}>Vouchers (deactivate)</h2>
                <div className="max-h-72 space-y-2 overflow-auto text-sm">
                  {(dash.voucherDefinitions || []).map((v) => (
                    <div key={v.id || v.code} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 bg-white/60 px-2 py-2">
                      <div>
                        <div className="font-mono font-medium">{v.code}</div>
                        <div className="text-xs text-slate-500">
                          {v.percent_off}% · used {v.used_count}/{v.max_uses || '∞'} · {v.active ? 'active' : 'inactive'}
                        </div>
                      </div>
                      {v.active ? (
                        <Button type="button" variant="outline" size="sm" className="text-red-700" disabled={opsVoucherBusy} onClick={() => void onDeleteVoucher(v.id)}>
                          <Trash2 className="mr-1 h-3.5 w-3.5" />
                          Off
                        </Button>
                      ) : (
                        <span className="text-xs text-slate-400">Inactive</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* CTO — latest stored PDF share URLs */}
            {role === 'CTO' && (
              <section className={`mb-8 ${theme.panel}`}>
                <h2 className={`mb-1 text-sm font-semibold ${theme.heading}`}>Latest generated reports (20)</h2>
                <p className={`mb-3 text-xs ${theme.sub}`}>
                  Plan, payment status, and timestamps come from <code className="rounded bg-slate-100 px-1">assessment_sessions</code> +{' '}
                  <code className="rounded bg-slate-100 px-1">students</code>. <em>Stored link</em> is <code className="rounded bg-slate-100 px-1">report_url</code>;{' '}
                  <em>API PDF</em> is built when <code className="rounded bg-slate-100 px-1">PUBLIC_API_ORIGIN</code> is set (same as shareable file URL).
                </p>
                <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                  <table className="w-full min-w-[980px] table-auto border-collapse text-left text-[11px] text-slate-800">
                    <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                      <tr>
                        <th className="min-w-[10.5rem] max-w-[13rem] px-2 py-2">Report time (IST)</th>
                        <th className="min-w-[7rem] max-w-[10rem] px-2 py-2">Student</th>
                        <th className="min-w-[6rem] max-w-[9rem] px-2 py-2">Plan</th>
                        <th className="min-w-[5rem] px-2 py-2">Pay</th>
                        <th className="min-w-[5.5rem] px-2 py-2">Session</th>
                        <th className="min-w-[10rem] max-w-[11rem] px-2 py-2">Paid (IST)</th>
                        <th className="min-w-0 max-w-[14rem] px-2 py-2">Stored URL</th>
                        <th className="min-w-0 max-w-[14rem] px-2 py-2">API PDF</th>
                        <th className="w-10 shrink-0 px-1 py-2 text-right" aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {(dash.recentReportUrls || []).length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-3 py-6 text-center text-sm text-slate-500">
                            No stored report URLs yet. After a PDF is generated and <code className="text-xs">report_url</code> is written, rows appear here.
                          </td>
                        </tr>
                      ) : (
                        (dash.recentReportUrls || []).map((row) => {
                          const fullUrl = String(row.report_url || '');
                          const fileViewUrl = String(row.file_view_url || '');
                          const label = shortReportUrlLabel(fullUrl);
                          const fileLabel = fileViewUrl ? shortReportUrlLabel(fileViewUrl) : '—';
                          const timeStr = formatIst(row.report_at);
                          const paidStr = row.session_paid_at ? formatIst(row.session_paid_at) : '—';
                          const planLabel = [row.plan_label || '', row.plan_id ? `(${row.plan_id})` : ''].filter(Boolean).join(' ') || '—';
                          return (
                            <tr key={`${row.student_id || ''}-${row.session_id || ''}`} className="border-t border-slate-100">
                              <td className="max-w-[13rem] align-top px-2 py-2">
                                <span className="block truncate font-mono text-[10px]" title={timeStr}>
                                  {timeStr}
                                </span>
                              </td>
                              <td className="max-w-[10rem] min-w-0 align-top px-2 py-2">
                                <span className="block truncate font-medium" title={row.student_name || undefined}>
                                  {row.student_name || '—'}
                                </span>
                              </td>
                              <td className="max-w-[9rem] min-w-0 align-top px-2 py-2" title={planLabel}>
                                <span className="block truncate">{planLabel}</span>
                              </td>
                              <td className="align-top px-2 py-2 font-medium">{row.student_payment_status || '—'}</td>
                              <td className="align-top px-2 py-2">{row.session_status || '—'}</td>
                              <td className="max-w-[11rem] align-top px-2 py-2">
                                <span className="block truncate font-mono text-[10px]" title={paidStr}>
                                  {paidStr}
                                </span>
                              </td>
                              <td className="min-w-0 max-w-[14rem] align-top px-2 py-2">
                                <div className="flex min-w-0 items-center gap-1">
                                  <a
                                    href={fullUrl || undefined}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="min-w-0 flex-1 truncate font-mono text-[10px] text-indigo-700 underline decoration-indigo-300 underline-offset-2 hover:text-indigo-900"
                                    title={fullUrl || undefined}
                                  >
                                    {label}
                                  </a>
                                </div>
                              </td>
                              <td className="min-w-0 max-w-[14rem] align-top px-2 py-2">
                                {fileViewUrl ? (
                                  <a
                                    href={fileViewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block truncate font-mono text-[10px] text-teal-700 underline decoration-teal-300 underline-offset-2 hover:text-teal-900"
                                    title={fileViewUrl}
                                  >
                                    {fileLabel}
                                  </a>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td className="px-1 py-2 align-middle text-right">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 shrink-0"
                                  title="Copy stored report URL"
                                  onClick={() => void navigator.clipboard.writeText(fullUrl)}
                                >
                                  <Copy className="h-3.5 w-3.5" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* CTO — event stream (filtered slice + breakdown, not a raw dump) */}
            {role === 'CTO' && (
              <section className={`mb-8 ${theme.panel}`}>
                <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className={`text-sm font-semibold ${theme.heading}`}>Event stream</h2>
                    <p className={`mt-1 max-w-3xl text-[11px] leading-relaxed ${theme.sub}`}>
                      Range filters use UTC timestamps that match <code className="rounded bg-slate-100 px-1">events.created_at</code>. Row times are shown in IST. Load at most 250 rows per request — narrow the window or event name to stay focused.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" size="sm" variant="outline" disabled={ctoEventsLoading} onClick={() => void fetchCtoEvents()}>
                      {ctoEventsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
                    </Button>
                    <Button type="button" size="sm" variant="secondary" onClick={() => void reloadEventsForCto()}>
                      Open in modal (800)
                    </Button>
                  </div>
                </div>

                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className={`text-[10px] font-semibold uppercase tracking-wide ${theme.sub}`}>Presets</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full text-xs"
                    onClick={() => {
                      setCtoEventsAllTime(false);
                      setCtoRangeStartMs(Date.now() - 24 * 3600000);
                      setCtoRangeEndMs(Date.now());
                    }}
                  >
                    24h
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full text-xs"
                    onClick={() => {
                      setCtoEventsAllTime(false);
                      setCtoRangeStartMs(Date.now() - 7 * 86400000);
                      setCtoRangeEndMs(Date.now());
                    }}
                  >
                    7d
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full text-xs"
                    onClick={() => {
                      setCtoEventsAllTime(false);
                      setCtoRangeStartMs(Date.now() - 30 * 86400000);
                      setCtoRangeEndMs(Date.now());
                    }}
                  >
                    30d
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={ctoEventsAllTime ? 'default' : 'outline'}
                    className="rounded-full text-xs"
                    onClick={() => setCtoEventsAllTime(true)}
                  >
                    All time
                  </Button>
                </div>

                <div className="mb-3 flex flex-wrap items-end gap-2 md:gap-3">
                  <label className={`flex flex-col gap-1 text-[10px] font-medium ${theme.sub}`}>
                    From (local)
                    <input
                      type="datetime-local"
                      disabled={ctoEventsAllTime}
                      className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-900 disabled:opacity-50"
                      value={toDatetimeLocalValue(ctoRangeStartMs)}
                      onChange={(e) => {
                        const ms = parseDatetimeLocalToMs(e.target.value);
                        if (ms == null) return;
                        setCtoEventsAllTime(false);
                        setCtoRangeStartMs(ms);
                      }}
                    />
                  </label>
                  <label className={`flex flex-col gap-1 text-[10px] font-medium ${theme.sub}`}>
                    To (local)
                    <input
                      type="datetime-local"
                      disabled={ctoEventsAllTime}
                      className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-900 disabled:opacity-50"
                      value={toDatetimeLocalValue(ctoRangeEndMs)}
                      onChange={(e) => {
                        const ms = parseDatetimeLocalToMs(e.target.value);
                        if (ms == null) return;
                        setCtoEventsAllTime(false);
                        setCtoRangeEndMs(ms);
                      }}
                    />
                  </label>
                  <label className={`flex min-w-[10rem] flex-1 flex-col gap-1 text-[10px] font-medium ${theme.sub}`}>
                    Event name contains
                    <input
                      className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-900"
                      placeholder="e.g. page_view, payment…"
                      value={eventFilter}
                      onChange={(e) => setEventFilter(e.target.value)}
                    />
                  </label>
                  <Button type="button" size="sm" className="rounded-lg" onClick={() => void fetchCtoEvents()}>
                    Apply filters
                  </Button>
                </div>

                {ctoEventsErr ? <p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{ctoEventsErr}</p> : null}

                {ctoEventSummary.length > 0 ? (
                  <div className="mb-3 rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2.5">
                    <p className={`mb-2 text-[10px] font-semibold uppercase tracking-wide ${theme.sub}`}>In this slice · by event name</p>
                    <div className="flex flex-wrap gap-1.5">
                      {ctoEventSummary.map(([name, count]) => (
                        <button
                          key={name}
                          type="button"
                          className="max-w-full rounded-full border border-slate-200 bg-white px-2.5 py-1 text-left text-[11px] text-slate-800 shadow-sm hover:border-indigo-300 hover:bg-indigo-50/60"
                          title={`Filter table to “${name}”`}
                          onClick={() => void fetchCtoEvents({ eventName: name })}
                        >
                          <span className="font-mono font-medium">{name}</span>
                          <span className="ml-1.5 text-slate-500">×{count}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                <p className={`mb-2 text-[11px] ${theme.sub}`}>
                  {ctoEventsLoading ? 'Loading…' : `${ctoEvents.length} row${ctoEvents.length === 1 ? '' : 's'} in view`}
                  {!ctoEventsAllTime ? (
                    <span className="ml-2 font-mono text-[10px] text-slate-500">
                      ({msToSqlUtc(ctoRangeStartMs)} → {msToSqlUtc(ctoRangeEndMs)} UTC)
                    </span>
                  ) : (
                    <span className="ml-2 text-[10px] text-slate-500">(no date filter)</span>
                  )}
                </p>

                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                  <div className="max-h-[min(52vh,560px)] overflow-auto overflow-x-auto">
                    <table className="w-full min-w-[920px] table-auto border-collapse text-left text-[11px] text-slate-800">
                      <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-[10px] uppercase text-slate-500 shadow-sm">
                        <tr>
                          <th className="min-w-[10.5rem] max-w-[12rem] px-2 py-2">IST time</th>
                          <th className="min-w-[9rem] max-w-[14rem] px-2 py-2">Event</th>
                          <th className="min-w-[4rem] max-w-[7rem] px-2 py-2">Source</th>
                          <th className="min-w-[6rem] max-w-[9rem] px-2 py-2">Student</th>
                          <th className="min-w-[6rem] max-w-[9rem] px-2 py-2">Session</th>
                          <th className="min-w-[6rem] px-2 py-2">Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ctoEventsLoading && ctoEvents.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-3 py-10 text-center text-sm text-slate-500">
                              <span className="inline-flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading events…
                              </span>
                            </td>
                          </tr>
                        ) : null}
                        {!ctoEventsLoading && !ctoEventsErr && ctoEvents.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-3 py-8 text-center text-sm text-slate-500">
                              No events for this range or name filter. Widen the dates, clear the name field, or try &quot;All time&quot; (still capped at 250 newest matches).
                            </td>
                          </tr>
                        ) : null}
                        {ctoEvents.map((ev) => {
                          const istTime = formatIst(ev.created_at);
                          return (
                          <tr key={String(ev.id)} className="border-t border-slate-100 align-top">
                            <td className="max-w-[12rem] min-w-0 align-top px-2 py-1.5">
                              <span className="block truncate font-mono text-[10px]" title={istTime}>
                                {istTime}
                              </span>
                            </td>
                            <td className="max-w-[14rem] min-w-0 align-top px-2 py-1.5">
                              <span className="block truncate font-medium" title={ev.event_name}>
                                {ev.event_name}
                              </span>
                            </td>
                            <td className="max-w-[7rem] min-w-0 align-top px-2 py-1.5">
                              <span className="block truncate text-slate-700" title={ev.source}>
                                {ev.source || '—'}
                              </span>
                            </td>
                            <td className="max-w-[9rem] min-w-0 align-top px-2 py-1.5 font-mono text-[10px]">
                              <span className="block truncate" title={ev.student_id || ''}>
                                {ev.student_id || '—'}
                              </span>
                            </td>
                            <td className="max-w-[9rem] min-w-0 align-top px-2 py-1.5 font-mono text-[10px]">
                              <span className="block truncate" title={ev.session_id || ''}>
                                {ev.session_id || '—'}
                              </span>
                            </td>
                            <td className="min-w-[8rem] max-w-[20rem] align-top px-2 py-1.5">
                              <JsonCell value={ev.event_data} />
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}

            {role !== 'CTO' && (role === 'CEO' || role === 'CMO') && (
              <section className={`mb-8 ${theme.panel}`}>
                <h2 className={`mb-2 text-sm font-semibold ${theme.heading}`}>Recent product events</h2>
                <Button type="button" size="sm" variant="outline" className="mb-2" onClick={() => void openDetail('Recent events (200)', '/api/admin/events?limit=200')}>
                  Open event list
                </Button>
                <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                      <tr>
                        <th className="px-2 py-2">IST</th>
                        <th className="px-2 py-2">Event</th>
                        <th className="px-2 py-2">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(dash.recentEvents || []).slice(0, 12).map((ev) => (
                        <tr key={String(ev.id)} className="border-t border-slate-100">
                          <td className="whitespace-nowrap px-2 py-1.5">{formatIst(ev.created_at)}</td>
                          <td className="px-2 py-1.5 font-medium">{ev.event_name}</td>
                          <td className="px-2 py-1.5">{ev.source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        ) : null}
      </div>

      <DetailModal
        open={detail.open}
        title={detail.title}
        loading={detailLoading}
        error={detailErr}
        onClose={closeDetail}
      >
        {Array.isArray(detailRows) && detailRows[0] && 'display_name' in detailRows[0] ? (
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-slate-100 text-[10px] uppercase text-slate-600">
              <tr>
                <th className="p-2">IST created</th>
                <th className="p-2">Name</th>
                <th className="p-2">Email</th>
                <th className="p-2">Flow</th>
                <th className="p-2">UTM src</th>
              </tr>
            </thead>
            <tbody>
              {detailRows.map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="whitespace-nowrap p-2 font-mono">{formatIst(s.created_at)}</td>
                  <td className="p-2">{s.display_name}</td>
                  <td className="max-w-[200px] truncate p-2">{s.email}</td>
                  <td className="p-2">{s.flow_source}</td>
                  <td className="p-2">{s.utm_source || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {Array.isArray(detailRows) && detailRows[0] && 'event_name' in detailRows[0] ? (
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-slate-100 text-[10px] uppercase text-slate-600">
              <tr>
                <th className="p-2">IST</th>
                <th className="p-2">Event</th>
                <th className="p-2">Src</th>
                <th className="p-2">Student</th>
                <th className="p-2">Payload</th>
              </tr>
            </thead>
            <tbody>
              {detailRows.map((ev) => (
                <tr key={String(ev.id)} className="border-t border-slate-100">
                  <td className="whitespace-nowrap p-2">{formatIst(ev.created_at)}</td>
                  <td className="p-2 font-medium">{ev.event_name}</td>
                  <td className="p-2">{ev.source}</td>
                  <td className="max-w-[90px] truncate p-2 font-mono">{ev.student_id || '—'}</td>
                  <td className="p-2">
                    <JsonCell value={ev.event_data} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {Array.isArray(detailRows) && detailRows[0] && 'redemption_id' in detailRows[0] ? (
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-slate-100 text-[10px] uppercase text-slate-600">
              <tr>
                <th className="p-2">IST</th>
                <th className="p-2">Code</th>
                <th className="p-2">%</th>
                <th className="p-2">Student</th>
                <th className="p-2">Session</th>
              </tr>
            </thead>
            <tbody>
              {detailRows.map((r) => (
                <tr key={String(r.redemption_id)} className="border-t border-slate-100">
                  <td className="whitespace-nowrap p-2">{formatIst(r.redeemed_at)}</td>
                  <td className="p-2 font-mono font-medium">{r.code}</td>
                  <td className="p-2">{r.percent_off}%</td>
                  <td className="max-w-[100px] truncate p-2 font-mono">{r.student_id || '—'}</td>
                  <td className="max-w-[100px] truncate p-2 font-mono">{r.session_id || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {Array.isArray(detailRows) && detailRows[0] && 'razorpay_order_id' in detailRows[0] ? (
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-slate-100 text-[10px] uppercase text-slate-600">
              <tr>
                <th className="p-2">IST</th>
                <th className="p-2">Status</th>
                <th className="p-2">Amount</th>
                <th className="p-2">Order</th>
              </tr>
            </thead>
            <tbody>
              {detailRows.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="whitespace-nowrap p-2">{formatIst(p.created_at)}</td>
                  <td className="p-2">{p.status}</td>
                  <td className="p-2">{rupeesFromPaise(p.amount_paise)}</td>
                  <td className="max-w-[160px] truncate p-2 font-mono">{p.razorpay_order_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {Array.isArray(detailRows) && detailRows[0] && 'student_name' in detailRows[0] ? (
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-slate-100 text-[10px] uppercase text-slate-600">
              <tr>
                <th className="p-2">IST updated</th>
                <th className="p-2">Status</th>
                <th className="p-2">Student</th>
                <th className="p-2">Grade</th>
                <th className="p-2">Session id</th>
              </tr>
            </thead>
            <tbody>
              {detailRows.map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="whitespace-nowrap p-2">{formatIst(s.updated_at)}</td>
                  <td className="p-2 font-medium">{s.status}</td>
                  <td className="p-2">{s.student_name}</td>
                  <td className="p-2">{s.assessment_grade}</td>
                  <td className="max-w-[120px] truncate p-2 font-mono">{s.id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </DetailModal>
    </div>
  );
}
